import React from 'react';
import { Link } from 'react-scroll';
import { useReveal } from '../lib/useReveal';
import { cn } from '../lib/cn';

export const Services = ({ data, language = 'bg' }) => {
  const headRef = useReveal();
  const gridRef = useReveal();

  const services = data?.services || [];

  return (
    <section id="uslugi" className="section-pad bg-paper-soft text-ink relative overflow-hidden">
      <div className="max-wrap section-wrap">
        <div ref={headRef} className="reveal grid grid-cols-12 gap-y-6 gap-x-6 items-end">
          <div className="col-span-12 lg:col-span-3">
            <span className="font-mono text-index text-ink-muted">02 / 04</span>
            <span className="block mt-4 h-px w-16 bg-ink/60" aria-hidden="true" />
          </div>
          <div className="col-span-12 lg:col-span-9">
            <h2 className="font-serif text-balance tracking-tightest leading-[0.98]"
                style={{ fontSize: 'clamp(2.25rem, 8vw, 6rem)' }}>
              {data?.title || (language === 'bg' ? 'Нашите услуги' : 'Our services')}
            </h2>
            {data?.desc && (
              <p className="mt-8 max-w-2xl text-ink-muted text-base sm:text-lg leading-relaxed text-pretty">
                {data.desc}
              </p>
            )}
          </div>
        </div>

        <ul ref={gridRef} className="reveal mt-14 sm:mt-20 grid grid-cols-1 md:grid-cols-2 border-t border-l border-rule">
          {services.map((service, i) => (
            <ServiceRow
              key={i}
              index={i}
              total={services.length}
              service={service}
            />
          ))}
        </ul>

        {data?.button && (
          <div className="mt-16 sm:mt-24 flex justify-end">
            <Link
              to="kontakti"
              smooth
              duration={700}
              offset={-40}
              className="group inline-flex items-center gap-4 cursor-pointer text-ink"
            >
              <span className="relative font-sans text-sm tracking-[0.18em] uppercase">
                {data.button}
                <span className="absolute left-0 -bottom-1 w-full h-px bg-ink transition-transform duration-500 ease-out-quart origin-right scale-x-100 group-hover:origin-left" />
              </span>
              <svg viewBox="0 0 24 24" fill="none" className="w-10 h-px overflow-visible text-ink transition-transform duration-500 ease-out-quart group-hover:translate-x-2" aria-hidden="true">
                <path stroke="currentColor" strokeWidth="1" strokeLinecap="square" d="M0 12h24m0 0l-6-6m6 6l-6 6" />
              </svg>
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};

const ServiceRow = ({ service, index, total }) => {
  const hasLink = Boolean(service.link);
  const Wrapper = hasLink ? 'a' : 'div';
  const wrapperProps = hasLink
    ? { href: service.link, target: '_blank', rel: 'noopener noreferrer' }
    : {};

  return (
    <li className="group relative border-b border-r border-rule">
      <Wrapper
        {...wrapperProps}
        className={cn(
          'relative flex flex-col h-full p-7 sm:p-9 transition-colors duration-500 hover:bg-paper',
          hasLink && 'cursor-pointer'
        )}
      >
        <div className="flex items-start justify-between gap-6">
          <div className="flex items-baseline gap-4 min-w-0">
            <span className="font-mono text-[0.72rem] tabular-nums tracking-[0.22em] text-ink-faint mt-1">
              {String(index + 1).padStart(2, '0')}
            </span>
            <h3 className="font-serif text-ink tracking-editorial leading-[1.1] text-balance"
                style={{ fontSize: 'clamp(1.375rem, 2.4vw, 1.875rem)' }}>
              {service.name}
            </h3>
          </div>

          {service.icon && (
            <span
              className="shrink-0 block w-8 h-8 bg-ink-faint group-hover:bg-brass-deep transition-colors duration-500"
              style={{
                WebkitMaskImage: `url(${service.icon})`,
                maskImage: `url(${service.icon})`,
                WebkitMaskRepeat: 'no-repeat',
                maskRepeat: 'no-repeat',
                WebkitMaskPosition: 'center',
                maskPosition: 'center',
                WebkitMaskSize: 'contain',
                maskSize: 'contain',
              }}
              aria-hidden="true"
            />
          )}
        </div>

        <p className="mt-5 text-ink-muted text-[0.92rem] leading-relaxed text-pretty">
          {service.text}
        </p>

        {hasLink && (
          <div className="mt-5 inline-flex items-center gap-2 text-ink-muted group-hover:text-brass-deep transition-colors duration-500">
            <svg viewBox="0 0 24 24" fill="none" className="w-4 h-4 transition-transform duration-500 group-hover:translate-x-1" aria-hidden="true">
              <path stroke="currentColor" strokeWidth="1.5" strokeLinecap="square" d="M7 17L17 7M17 7H9M17 7V15" />
            </svg>
          </div>
        )}

        <span
          className="absolute left-0 bottom-[-1px] h-px w-0 bg-brass-deep transition-all duration-700 ease-out-quart group-hover:w-full"
          aria-hidden="true"
        />
      </Wrapper>

      <span className="sr-only">
        {index + 1} of {total}
        {hasLink && ' — opens catalog in a new tab'}
      </span>
    </li>
  );
};
